import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, Lock } from "lucide-react";
import SiteSettings from "./admin/SiteSettings";
import GalleryManage from "./admin/GalleryManage";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [authToken, setAuthToken] = useState("");

  useEffect(() => {
    const savedToken = localStorage.getItem("adminToken");

    if (savedToken) {
      setAuthToken(savedToken);
      setIsAuthenticated(true);
      fetchQuotes(savedToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });

      const token = response.data.token;

      setAuthToken(token);
      localStorage.setItem("adminToken", token);
      setIsAuthenticated(true);
      fetchQuotes(token);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuotes = async (token) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(response.data || []);
    } catch (err) {
      setError("Failed to fetch quotes");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setError("");

      const response = await axios.get(`${API}/admin/quotes/export`, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "quotes.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);

      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail || err?.message || "Unknown error";

      setError(`Export failed (${status || "no status"}): ${detail}`);
    }
  };

  const handleClearQuotes = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete all quote requests? This cannot be undone."
    );

    if (!confirmed) return;

    try {
      setError("");

      await axios.delete(`${API}/admin/quotes`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setQuotes([]);
    } catch (err) {
      console.error("Failed to clear quotes:", err);

      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail || err?.message || "Unknown error";

      setError(`Failed to clear quotes (${status || "no status"}): ${detail}`);
    }
  };

  const handleLogout = async () => {
    try {
      if (authToken) {
        await axios.post(
          `${API}/admin/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
      }
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      localStorage.removeItem("adminToken");
      setIsAuthenticated(false);
      setPassword("");
      setAuthToken("");
      setQuotes([]);
      setError("");
    }
  };

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchQuotes(authToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authToken]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary/30 to-white">
        <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-soft border border-border">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">
              Admin Login
            </h1>
            <p className="text-muted-foreground">
              Enter your password to access the admin panel
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-lg"
              />
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-primary underline"
            >
              Forgot Password?
            </button>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6"
            >
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">Manage your website</p>
            </div>
            <Button
              type="button"
              onClick={handleLogout}
              className="rounded-full bg-primary hover:bg-primary/90 text-white"
            >
              <LogOut className="mr-2" size={16} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <Tabs defaultValue="quotes" className="space-y-6">
          <TabsList className="bg-white border border-border p-1">
            <TabsTrigger value="quotes">Quote Requests</TabsTrigger>
            <TabsTrigger value="settings">Site Settings</TabsTrigger>
            <TabsTrigger value="gallery">Gallery</TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <div className="bg-white rounded-2xl border border-border p-6">
              {error && (
                <div className="mb-4 bg-red-100 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-primary mb-2">
                    Quote Requests
                  </h2>
                  <p className="text-muted-foreground">Total: {quotes.length}</p>
                </div>

                <Button
                  type="button"
                  onClick={handleClearQuotes}
                  variant="destructive"
                  className="rounded-full"
                >
                  Clear Quotes
                </Button>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : quotes.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No quote requests yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Services</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes.map((quote, index) => (
                        <TableRow key={quote.id || index}>
                          <TableCell className="whitespace-nowrap">
                            {quote.created_at
                              ? new Date(quote.created_at).toLocaleDateString()
                              : ""}
                          </TableCell>
                          <TableCell>{quote.name}</TableCell>
                          <TableCell>{quote.email}</TableCell>
                          <TableCell>{quote.phone}</TableCell>
                          <TableCell>{quote.property_type}</TableCell>
                          <TableCell>{quote.size}</TableCell>
                          <TableCell>{quote.frequency}</TableCell>
                          <TableCell>
                            <div
                              className="max-w-xs truncate"
                              title={(quote.services || []).join(", ")}
                            >
                              {(quote.services || []).join(", ")}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SiteSettings authToken={authToken} />
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <GalleryManage authToken={authToken} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
